import { afterNextRender, Component, computed, inject, signal, viewChild, PLATFORM_ID, input, OnDestroy } from '@angular/core';
import { CommonModule, isPlatformBrowser } from "@angular/common";
import { BlogService } from '../blog.service';
import { DetailBlogPost } from '../blog.types';
import { finalize, tap } from 'rxjs';
import { DomSanitizer, Meta, SafeHtml, Title } from '@angular/platform-browser';
import { PostComponent } from '../post/post.component';
import { environment } from '../../../../environments/environment';
import { formatDateToReadableString } from '../../../shared/utils/formatting';
import { Router } from '@angular/router';
import { StockService } from '../../../stock/stock.service';
import { ResolutionService } from '../../../shared/resolution.service';
import { SignalsStoreService } from '../../../shared/signals-store.service';
import { ModalContentData } from '../../../shared/modal-content/modal-content';
import { ModalContentTypes } from '../../../shared/constants/modal-content-types';
import { ModalContentService } from '../../../shared/modal-content/modal-content.service';
import { CarouselComponent } from '../../../shared/carousel/carousel.component';
import { PagesService } from '../../pages.service';

@Component({
  selector: 'app-blog-post',
  imports: [
    CommonModule,
    PostComponent,
    CarouselComponent
  ],
  templateUrl: './blog-post.component.html',
  styleUrl: './blog-post.component.scss'
})
export class BlogPostComponent implements OnDestroy {

  //#region Services

  #meta = inject(Meta);

  #title = inject(Title);

  #blogService = inject(BlogService);

  #sanitizer = inject(DomSanitizer);

  #route = inject(Router);

  #resolutionService = inject(ResolutionService);

  #signalsStoreService = inject(SignalsStoreService);

  #modalContentService = inject(ModalContentService);

  #pagesService = inject(PagesService);

  #platformId = inject(PLATFORM_ID);

  //#endregion

  //#region Properties

  // Input from resolved data
  blogPost = input<DetailBlogPost | null>(null);

  blogPostDetail = signal<DetailBlogPost | null>(null);

  isMobile = computed(() => this.#resolutionService.isMobile());

  featuredProducts = viewChild<CarouselComponent>('featuredProducts');

  isLoading = signal<boolean>(true);

  backgroundImage = computed(() => this.#getBgImage());

  //#endregion

  //#region Constructor

  constructor() {
    this.#pagesService.restartSideMenu();
    this.#pagesService.showBreadcrumb.set(false);
    this.#signalsStoreService.isInBlogPost.set(true);
    // Set basic meta tags immediately for SSR
    this.#setBasicMetaTags();
    
    // Process resolved data immediately if available
    const resolvedData = this.blogPost();
    if (resolvedData) {
      this.#processBlogPostData(resolvedData);
    } else {
      // Fallback to loading data if resolver didn't provide it
      this.#getBlogPostDetail();
    }
  }

  ngOnDestroy() {
    this.#pagesService.showBreadcrumb.set(true);
    this.#signalsStoreService.showCartPreviewInBLogPost.set(false);
  }

  //#endregion

  //#region Methods

  #setBasicMetaTags() {
    // Extract blog post info from URL for basic meta tags
    const urlParts = this.#route.url.split('/');
    const category = urlParts[urlParts.length - 2];
    const title = urlParts[urlParts.length - 1];
    
    // Decode URL-encoded title
    const decodedTitle = decodeURIComponent(title).replace(/%20/g, ' ');
    
    console.log('Setting basic meta tags for:', decodedTitle, 'URL:', this.#route.url);
    
    // Set basic meta tags that will be available during SSR
    this.#title.setTitle(`${decodedTitle} | The Produce Box Blog`);
    
    // Basic Open Graph tags
    this.#meta.addTag({ property: 'og:title', content: `${decodedTitle} | The Produce Box Blog` });
    this.#meta.addTag({ property: 'og:description', content: `Read about ${decodedTitle} on The Produce Box blog. Discover fresh, local produce and healthy recipes.` });
    this.#meta.addTag({ property: 'og:type', content: 'article' });
    this.#meta.addTag({ property: 'og:site_name', content: 'The Produce Box' });
    this.#meta.addTag({ property: 'og:url', content: `${environment.config.seo.baseURL}${this.#route.url}` });
    this.#meta.addTag({ property: 'og:image', content: `${environment.config.seo.baseURL}assets/images/product-placeholder.webp` });
    this.#meta.addTag({ property: 'og:image:width', content: '1200' });
    this.#meta.addTag({ property: 'og:image:height', content: '630' });
    this.#meta.addTag({ property: 'og:image:alt', content: `Featured image for ${decodedTitle}` });
    
    // Basic Twitter Card tags
    this.#meta.addTag({ name: 'twitter:card', content: 'summary_large_image' });
    this.#meta.addTag({ name: 'twitter:title', content: `${decodedTitle} | The Produce Box Blog` });
    this.#meta.addTag({ name: 'twitter:description', content: `Read about ${decodedTitle} on The Produce Box blog. Discover fresh, local produce and healthy recipes.` });
    this.#meta.addTag({ name: 'twitter:image', content: `${environment.config.seo.baseURL}assets/images/product-placeholder.webp` });
    this.#meta.addTag({ name: 'twitter:site', content: '@TheProduceBox' });
    
    // Basic HTML meta tags
    this.#meta.addTag({ name: 'description', content: `Read about ${decodedTitle} on The Produce Box blog. Discover fresh, local produce and healthy recipes.` });
    this.#meta.addTag({ name: 'robots', content: 'index, follow' });
  }

  #processBlogPostData(data: DetailBlogPost) {
    this.blogPostDetail.set({
      ...data,
      content: typeof data.content === 'string' ? this.sanitizeHtml(data.content) : data.content,
      featuredProducts: data.featuredProducts.map((x: any) => ({ 
        content: { productDetails: x } 
      })),
      publishDate: formatDateToReadableString(data.publishDate).readableString
    });

    this.#setUpSEOTags();
    this.isLoading.set(false);
  }

  #getId() {
    const arr = this.#route.url.split('/');
    return arr.splice(arr.length - 2, arr.length).join('/');
  }

  #getBlogPostDetail() {
    this.blogPostDetail.set(null);
    this.#blogService
      .getById(this.#getId())
      .pipe(
        tap(response => {
          if (!response.data) return;
          this.#processBlogPostData(response.data as DetailBlogPost);
        }),
        finalize(() => this.isLoading.set(false))
      )
      .subscribe();
  }

  sanitizeHtml(html: string): SafeHtml {
    // Only use DOMParser in browser environment
    if (isPlatformBrowser(this.#platformId)) {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      return this.#sanitizer.bypassSecurityTrustHtml(doc.body.innerHTML);
    }
    
    // For SSR, return the HTML as-is (it will be sanitized by Angular)
    return this.#sanitizer.bypassSecurityTrustHtml(html);
  }

  #getAbsoluteImageUrl(imageUrl: string | undefined): string {
    if (!imageUrl) {
      return `${environment.config.seo.baseURL}assets/images/product-placeholder.webp`;
    }
    
    // If the image URL is already absolute, return it as is
    if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
      return imageUrl;
    }
    
    // If the image URL is relative, make it absolute
    if (imageUrl.startsWith('/')) {
      return `${environment.config.seo.baseURL}${imageUrl.substring(1)}`;
    }
    
    // If the image URL doesn't start with '/', add it
    return `${environment.config.seo.baseURL}${imageUrl}`;
  }

  /**
   * Sets up comprehensive SEO and social media meta tags for the blog post.
   * This includes Open Graph tags for Facebook/LinkedIn sharing, Twitter Cards,
   * and article-specific meta tags for better social media previews.
   * This method works both in SSR and browser environments.
   */
  #setUpSEOTags() {

    const { metaTitle, metaDescription, metaKeywords } = this.blogPostDetail()?.SEO ?? { metaTitle: '', metaDescription: '', metaKeywords: '' };
    const blogPost = this.blogPostDetail();
    
    console.log('Setting up SEO tags for blog post:', blogPost?.title, 'Meta title:', metaTitle);
    
    if (!blogPost) return;

    // Update existing meta tags with actual blog post data
    this.#meta.updateTag({ name: 'description', content: metaDescription });
    this.#meta.updateTag({ name: 'keywords', content: (metaKeywords ?? '').replaceAll(' ', ', ') });
    this.#meta.updateTag({ name: 'title', content: metaTitle });
    this.#meta.updateTag({ name: 'author', content: blogPost.author });

    // Page title
    this.#title.setTitle(metaTitle);

    // Ensure image URL is absolute for social sharing
    const imageUrl = this.#getAbsoluteImageUrl(blogPost.image);
    const pageUrl = `${environment.config.seo.baseURL}${blogPost.url}`;

    // Update Open Graph tags with actual blog post data
    this.#meta.updateTag({ property: 'og:title', content: metaTitle });
    this.#meta.updateTag({ property: 'og:description', content: metaDescription });
    this.#meta.updateTag({ property: 'og:url', content: pageUrl });
    this.#meta.updateTag({ property: 'og:image', content: imageUrl });
    this.#meta.updateTag({ property: 'og:image:alt', content: `Featured image for ${blogPost.title}` });
    this.#meta.addTag({ property: 'og:locale', content: 'en_US' });

    // Article-specific Open Graph tags
    this.#meta.addTag({ property: 'article:author', content: blogPost.author });
    this.#meta.addTag({ property: 'article:published_time', content: blogPost.publishDate });
    this.#meta.addTag({ property: 'article:section', content: blogPost.category?.name || 'Blog' });
    this.#meta.addTag({ property: 'article:tag', content: blogPost.category?.name || '' });

    // Update Twitter Card meta tags with actual blog post data
    this.#meta.updateTag({ name: 'twitter:title', content: metaTitle });
    this.#meta.updateTag({ name: 'twitter:description', content: metaDescription });
    this.#meta.updateTag({ name: 'twitter:image', content: imageUrl });
    this.#meta.updateTag({ name: 'twitter:image:alt', content: `Featured image for ${blogPost.title}` });
    this.#meta.addTag({ name: 'twitter:creator', content: '@TheProduceBox' });

    // Additional Open Graph tags for better platform compatibility
    this.#meta.addTag({ property: 'og:image:type', content: 'image/jpeg' });
    this.#meta.addTag({ property: 'og:image:secure_url', content: imageUrl });
    this.#meta.addTag({ property: 'og:updated_time', content: blogPost.publishDate });
    
    // Additional Twitter tags for better compatibility
    this.#meta.addTag({ name: 'twitter:domain', content: 'theproducebox.com' });
    this.#meta.addTag({ name: 'twitter:url', content: pageUrl });
  }

  handleImageError(event: Event) {
    (event.target as HTMLImageElement).src = 'assets/images/product-placeholder.webp';
  }

  goToBlogs() {
    this.#route.navigate(['/pages/blog']);
  }

  addAllProducts(event: any) {

    if (!this.#signalsStoreService.hasSession())
      this.#openModalCheckAddress();

    this.featuredProducts()?.addAllProducts(event);
  }

  #openModalCheckAddress() {

    const data: ModalContentData = {
      title: 'Choose your delivery preference',
      textContent:
        `Before you start shopping, let's make sure you're located near one of our delivery routes or pick up locations`,
      closeable: false
    };

    this.#modalContentService
      .openModal(ModalContentTypes.CHECK_ADDRESS, data, undefined);
  }

  #getBgImage() {

    if (!this.blogPostDetail())
      return '';

    const { desktop, mobile } = this.blogPostDetail()?.banners ?? {};

    let img = '';

    if (this.isMobile() && mobile)
      img = mobile;
    else if (!this.isMobile() && desktop)
      img = desktop;

    if (!img)
      return '';

    return `url(${img})`;
  }

  //#endregion
}
